<template lang="html">
	<section
		ref="section"
		class="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-b from-background via-white to-surface"
	>
		<div
			class="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 animate-float"
		></div>
		<div
			class="absolute bottom-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl translate-y-1/2 animate-float-delayed"
		></div>
		<div
			class="absolute top-1/2 left-1/2 w-64 h-64 bg-brand-cyan/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse-slow"
		></div>

		<div class="relative max-w-screen-xl px-4 mx-auto lg:px-6">
			<div class="mb-16 lg:mb-24 text-center space-y-6">
				<div
					ref="badge"
					class="inline-flex items-center px-5 py-2.5 space-x-2 text-sm font-semibold text-primary bg-primary/5 rounded-full border border-primary/20 backdrop-blur-sm transition-all duration-700 opacity-0 translate-y-4"
				>
					<div
						class="w-2 h-2 bg-accent rounded-full animate-pulse"
					></div>
					<span>Why Choose Nexus</span>
				</div>

				<div class="space-y-5">
					<h2
						ref="heading"
						class="text-4xl md:text-5xl lg:text-6xl font-extrabold text-textPrimary tracking-tight transition-all duration-700 opacity-0 translate-y-6"
					>
						We didn't reinvent the wheel
					</h2>
					<p
						ref="subheading"
						class="max-w-3xl mx-auto text-base md:text-lg text-textSecondary lg:text-xl leading-relaxed transition-all duration-700 opacity-0 translate-y-6"
					>
						We made insurance simpler and more transparent with
						<span class="font-semibold text-primary"
							>your one-stop platform</span
						>
						to find, compare, buy, and manage it all.
					</p>
				</div>
			</div>

			<div
				ref="dashboardImageContainer"
				class="mb-16 lg:mb-24 transition-all duration-700 opacity-0 translate-y-6"
			>
				<div class="relative max-w-6xl mx-auto">
					<img
						src="/hero.png"
						alt="Nexus Dashboard Interface"
						class="w-full h-auto rounded-xl shadow-2xl border border-border/70 transform hover:scale-[1.01] transition duration-500 ease-in-out"
					/>
					<div
						class="absolute inset-0 rounded-xl pointer-events-none ring-4 ring-inset ring-primary/5"
					></div>
				</div>
			</div>
			<div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
				<div
					ref="card1"
					class="group relative p-8 transition-all duration-500 bg-white rounded-3xl border border-border hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-2 opacity-0 translate-y-8"
				>
					<div
						class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
					></div>
					<div class="relative">
						<div
							class="inline-flex p-4 mb-6 rounded-2xl bg-gradient-to-br from-primary to-primary-light shadow-lg shadow-primary/20 group-hover:shadow-xl group-hover:shadow-primary/30 transition-all duration-300 group-hover:scale-110"
						>
							<svg
								class="w-8 h-8 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
								></path>
							</svg>
						</div>
						<h3
							class="mb-3 text-xl font-bold text-textPrimary group-hover:text-primary transition-colors duration-300"
						>
							Secure & Protected
						</h3>
						<p class="text-textSecondary">
							Bank-level encryption ensures your data is always
							safe and protected.
						</p>
					</div>
				</div>

				<div
					ref="card2"
					class="group relative p-8 transition-all duration-500 bg-white rounded-3xl border border-border hover:border-accent/30 hover:shadow-2xl hover:shadow-accent/5 hover:-translate-y-2 opacity-0 translate-y-8"
				>
					<div
						class="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
					></div>
					<div class="relative">
						<div
							class="inline-flex p-4 mb-6 rounded-2xl bg-gradient-to-br from-accent to-accent-light shadow-lg shadow-accent/20 group-hover:shadow-xl group-hover:shadow-accent/30 transition-all duration-300 group-hover:scale-110"
						>
							<svg
								class="w-8 h-8 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13 10V3L4 14h7v7l9-11h-7z"
								></path>
							</svg>
						</div>
						<h3
							class="mb-3 text-xl font-bold text-textPrimary group-hover:text-accent transition-colors duration-300"
						>
							Instant Quotes
						</h3>
						<p class="text-textSecondary">
							Get personalized insurance quotes in under 60
							seconds.
						</p>
					</div>
				</div>

				<div
					ref="card3"
					class="group relative p-8 transition-all duration-500 bg-white rounded-3xl border border-border hover:border-brand-cyan/30 hover:shadow-2xl hover:shadow-brand-cyan/5 hover:-translate-y-2 opacity-0 translate-y-8"
				>
					<div
						class="absolute inset-0 bg-gradient-to-br from-brand-cyan/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
					></div>
					<div class="relative">
						<div
							class="inline-flex p-4 mb-6 rounded-2xl bg-gradient-to-br from-brand-cyan to-blue-400 shadow-lg shadow-brand-cyan/20 group-hover:shadow-xl group-hover:shadow-brand-cyan/30 transition-all duration-300 group-hover:scale-110"
						>
							<svg
								class="w-8 h-8 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
								></path>
							</svg>
						</div>
						<h3
							class="mb-3 text-xl font-bold text-textPrimary group-hover:text-brand-cyan transition-colors duration-300"
						>
							Expert Support
						</h3>
						<p class="text-textSecondary">
							Our team is here to guide you every step of the way.
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'FeaturesSection',
	mounted() {
		this.initScrollAnimations();
	},
	methods: {
		initScrollAnimations() {
			const options = {
				root: null,
				threshold: 0.1,
				rootMargin: '0px 0px -50px 0px',
			};

			const observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.animateElements();
						observer.disconnect();
					}
				});
			}, options);

			if (this.$refs.section) {
				observer.observe(this.$refs.section);
			}
		},
		animateElements() {
			setTimeout(() => {
				if (this.$refs.badge) {
					this.$refs.badge.style.opacity = '1';
					this.$refs.badge.style.transform = 'translateY(0)';
				}
			}, 100);

			setTimeout(() => {
				if (this.$refs.heading) {
					this.$refs.heading.style.opacity = '1';
					this.$refs.heading.style.transform = 'translateY(0)';
				}
			}, 300);

			setTimeout(() => {
				if (this.$refs.subheading) {
					this.$refs.subheading.style.opacity = '1';
					this.$refs.subheading.style.transform = 'translateY(0)';
				}
			}, 500);

			setTimeout(() => {
				if (this.$refs.dashboardImageContainer) {
					this.$refs.dashboardImageContainer.style.opacity = '1';
					this.$refs.dashboardImageContainer.style.transform =
						'translateY(0)';
				}
			}, 700);

			const cards = [
				this.$refs.card1,
				this.$refs.card2,
				this.$refs.card3,
			];
			cards.forEach((card, index) => {
				setTimeout(
					() => {
						if (card) {
							card.style.opacity = '1';
							card.style.transform = 'translateY(0)';
						}
					},
					900 + index * 150,
				);
			});
		},
	},
	beforeUnmount() {},
};
</script>
